import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { Modal, ModalContent } from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  ArrowUpRight,
  BarChart3,
  Check,
  ChevronRight,
  CircleAlert,
  CreditCard,
  Crown,
  FileText,
  LogOut,
  Palette,
  ShieldCheck,
  Smartphone,
  Mail,
} from 'lucide-react';

import { GET_ME } from '../graphql/queries';
import { trackEvent, trackPageView } from '../utils/gt';
import { hasConsent, grantConsent, revokeConsent, CONSENT_CHANGED_EVENT } from '../utils/cookieConsent';
import { formatDay, proPrice, subscriptionState, trialContext } from '../utils/subscription';
import { COMPANY_VARS } from '../utils/legal';
import { useThemePreference } from '../utils/theme';
import { useAppOnPhone } from '../utils/useAppOnPhone';
import { unsubscribeThisDevice } from '../utils/push';
import { forgetReads } from '../utils/quietRefresh';
import { clearScrollMemory } from '../utils/scrollMemory';
import { InstagramGlyph, INSTAGRAM_HANDLE, INSTAGRAM_URL } from '../components/Footer';
import { useInstallApp, usePush } from '../components/app/pwaContext';
import { ProFeatureList } from '../components/subscription/ProFeatureList';
import '../styles/pages/conta.css';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');

// The settings, in order. Phones list them one after another; from 960px an index picks the one open beside it.
const SECTIONS = [
  // Only while there's something to do on this device: installing it, or the notifications of the installed app
  { key: 'app', Icon: Smartphone },
  { key: 'subscription', Icon: Crown },
  { key: 'appearance', Icon: Palette },
  { key: 'privacy', Icon: ShieldCheck },
  // The footer's doors, in the installed app on a phone: it has no footer
  { key: 'contact', Icon: Mail },
];

const THEMES = [
  ['system', 'themeSystem'],
  ['light', 'themeLight'],
  ['dark', 'themeDark'],
];

// A small screen drawn in the app's light or dark colors (the theme previews)
const MockScreen = ({ dark = false }) => (
  <span className={`mt-look__screen${dark ? ' mt-look__screen--dark' : ''}`}>
    <i className="mt-look__line mt-look__line--title" />
    <i className="mt-look__card" />
    <i className="mt-look__line" />
    <i className="mt-look__line mt-look__line--short" />
    <i className="mt-look__bar" />
  </span>
);

// One label and its value, as a row of a grouped list
const Fact = ({ label, children }) => (
  <div className="mt-glist__row">
    <dt className="mt-glist__k">{label}</dt>
    <dd className="mt-glist__v">{children}</dd>
  </div>
);

export const Account = () => {
  const { t, i18n } = useTranslation();
  const appOnPhone = useAppOnPhone();
  const navigate = useNavigate();
  const account = (key, options) => t(`pages.Account.${key}`, options);
  const ui = (key, options) => t(`pages.Account.ui.${key}`, options);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [cookiesEnabled, setCookiesEnabled] = useState(hasConsent());
  const [themePreference, setThemePreference] = useThemePreference();
  const [section, setSection] = useState('subscription');
  const [isSignOutOpen, setSignOutOpen] = useState(false);
  // The breakpoint where the index and the open section sit side by side
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });

  const { client, data: dataMe, loading: isLoadingMe } = useQuery(GET_ME);
  const { canOffer, install } = useInstallApp();
  const push = usePush();
  const [isPushSaving, setPushSaving] = useState(false);

  useEffect(() => {
    trackPageView('account', `${t('appUrl')}/account`, '/account');
  }, [t]);

  useEffect(() => {
    const handler = (e) => setCookiesEnabled(e.detail.consent);
    window.addEventListener(CONSENT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
  }, []);

  // Coming back from the Stripe portal with the back button restores this page from cache: stop the spinner
  useEffect(() => {
    const reset = (event) => event.persisted && setIsPortalLoading(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const me = dataMe?.me;
  const { isPro, isTrial, isCanceling, isPastDue, hasSubscription } = subscriptionState(me);
  const lang = t('lang');
  const price = proPrice(lang);
  const periodEnd = formatDay(me?.subscriptionCurrentPeriodEnd, lang);
  const context = trialContext(me);

  const handleManageSubscription = async () => {
    trackEvent('account_manage_subscription', 'Account - Manage Subscription', 'account', true);
    setIsPortalLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/account`,
        }),
      });
      const { url } = response.ok ? await response.json() : {};
      if (!url) throw new Error('No portal url');
      window.location.href = url;
    } catch {
      setIsPortalLoading(false);
      toast.error(t('notifications.subscriptionPortalError'), { toastId: 'subscription-portal-error' });
    }
  };

  const trackUpgrade = () => trackEvent('account_upgrade_to_pro', 'Account - Upgrade to Pro', 'account', true);

  const logout = () => {
    trackEvent('account_logout', 'Account - Logout', 'account', true);
    // A shared phone: this device stops getting the person's notifications. Needs the token that goes next
    unsubscribeThisDevice(localStorage.getItem('authToken'));
    client.clearStore();
    forgetReads();
    clearScrollMemory();
    localStorage.removeItem('authToken');
    navigate('/');
  };

  const initial = (me?.name || '?').trim().charAt(0).toUpperCase();

  /* ── Assinatura: the plan as label / value rows, then what to do about it ── */

  // Stripe's portal: payment method, invoices and cancelling. Rows with a line under the title are named by the
  // title alone and described by the line
  const portalRow = (go = false) => (
    <button
      type="button"
      className={`mt-glist__row mt-glist__btn${go ? ' mt-glist__btn--go' : ''}`}
      onClick={handleManageSubscription}
      disabled={isPortalLoading}
      aria-busy={isPortalLoading || undefined}
      aria-labelledby={go ? undefined : 'mt-acct-manage-title'}
      aria-describedby={go ? undefined : 'mt-acct-manage-hint'}
    >
      {go && (
        <span className="mt-glist__sq mt-glist__sq--go" aria-hidden="true">
          <CreditCard />
        </span>
      )}
      <span className="mt-glist__k">
        <span id={go ? undefined : 'mt-acct-manage-title'} className="mt-glist__title">
          {go ? ui('updatePayment') : ui('manage')}
        </span>
        {!go && (
          <span id="mt-acct-manage-hint" className="mt-glist__hint">
            {ui('manageHint')}
          </span>
        )}
      </span>
      {isPortalLoading ? (
        <span className="mt-btn__spinner" aria-hidden="true" />
      ) : (
        <ArrowUpRight className="mt-glist__end" aria-hidden="true" />
      )}
    </button>
  );

  const pill = (label, tone = '') => <span className={`mt-acct-pill${tone ? ` mt-acct-pill--${tone}` : ''}`}>{label}</span>;
  const proName = <b>{ui('proName')}</b>;

  let subscriptionRows;
  let subscriptionFoot;
  let subscriptionValue;
  if (isPastDue) {
    // The plan is still "pro" while Stripe retries, but nothing of Pro works until it's paid: say it's on hold
    subscriptionRows = (
      <>
        <div className="mt-glist__row mt-glist__warn" role="status">
          <CircleAlert aria-hidden="true" />
          <span>{ui('pastDue')}</span>
        </div>
        <dl>
          <Fact label={ui('plan')}>
            <b>{ui('pastDueName')}</b>
          </Fact>
          <Fact label={ui('status')}>{pill(ui('statusPastDue'), 'warn')}</Fact>
        </dl>
        {portalRow(true)}
      </>
    );
    subscriptionFoot = ui('footPastDue');
    subscriptionValue = ui('indexPastDue');
  } else if (isCanceling) {
    // Cancelled in the portal: Pro (or the trial) runs to the end of the period, then the account goes back to free
    subscriptionRows = (
      <>
        <dl>
          <Fact label={ui('plan')}>{proName}</Fact>
          <Fact label={ui('status')}>{pill(isTrial ? ui('statusTrialCanceled') : ui('statusCanceled'), 'neutral')}</Fact>
          {periodEnd && <Fact label={ui('proUntil')}>{periodEnd}</Fact>}
        </dl>
        {portalRow()}
      </>
    );
    subscriptionFoot = periodEnd && ui(isTrial ? 'footTrialCanceling' : 'footCanceling', { date: periodEnd });
    subscriptionValue = isTrial ? ui('statusTrialCanceled') : ui('statusCanceled');
  } else if (isTrial) {
    subscriptionRows = (
      <>
        <dl>
          <Fact label={ui('plan')}>{proName}</Fact>
          <Fact label={ui('status')}>{pill(ui('statusTrialing'))}</Fact>
          {periodEnd && <Fact label={ui('trialUntil')}>{periodEnd}</Fact>}
          <Fact label={ui('after')}>{ui('perMonth', { price })}</Fact>
        </dl>
        {portalRow()}
      </>
    );
    subscriptionFoot = periodEnd ? ui('footTrial', { price, date: periodEnd }) : ui('footPro');
    subscriptionValue = ui('statusTrialing');
  } else if (isPro) {
    subscriptionRows = (
      <>
        <dl>
          <Fact label={ui('plan')}>{proName}</Fact>
          <Fact label={ui('status')}>{pill(ui('statusActive'))}</Fact>
          {periodEnd && <Fact label={ui('nextCharge')}>{ui('nextChargeValue', { date: periodEnd, price })}</Fact>}
        </dl>
        {portalRow()}
      </>
    );
    subscriptionFoot = ui('footPro');
    subscriptionValue = ui('proShort');
  } else {
    subscriptionRows = (
      <>
        <dl>
          <Fact label={ui('plan')}>
            <b>{ui('freeName')}</b>
          </Fact>
        </dl>
        <Link
          className="mt-glist__row mt-glist__btn mt-glist__btn--go"
          to="/plans"
          onClick={trackUpgrade}
          aria-labelledby="mt-acct-try-title"
          aria-describedby="mt-acct-try-hint"
        >
          <span className="mt-glist__sq mt-glist__sq--go" aria-hidden="true">
            <Crown />
          </span>
          <span className="mt-glist__k">
            <span id="mt-acct-try-title" className="mt-glist__title">
              {account('discoverPro', { context })}
            </span>
            <span id="mt-acct-try-hint" className="mt-glist__hint">
              {ui('tryProHint', { context, price })}
            </span>
          </span>
          <ChevronRight className="mt-glist__end" aria-hidden="true" />
        </Link>
      </>
    );
    subscriptionFoot = ui('footFree');
    subscriptionValue = ui('freeName');
  }

  // Desktop only: the open section has the room to list what Pro includes
  const proExtras = isDesktop && !isLoadingMe && !isPastDue && (
    <>
      <h3 id="mt-acct-pro-list" className="mt-acct__sub">
        {hasSubscription ? ui('included') : ui('whatProIncludes')}
      </h3>
      <ProFeatureList labelledBy="mt-acct-pro-list" className="mt-feats--two-wide" />
      {!hasSubscription && (
        <div className="mt-acct__actions">
          <Link className="mt-btn mt-btn--primary mt-acct__cta" to="/plans" onClick={trackUpgrade}>
            {account('discoverPro', { context })}
          </Link>
          <Link
            className="mt-acct__link"
            to="/plans"
            onClick={() => trackEvent('account_see_plans', 'Account - See Plans', 'account', true)}
          >
            {ui('seePlans')}
            <ChevronRight aria-hidden="true" />
          </Link>
        </div>
      )}
    </>
  );

  const togglePush = async (isOn) => {
    if (isOn) {
      push.openOptIn('conta');
      return;
    }
    setPushSaving(true);
    await push.disable();
    setPushSaving(false);
  };

  const isPushBlocked = push.permission === 'denied' && !push.subscribed;
  const sections = SECTIONS.filter(({ key }) => (key !== 'app' || canOffer || push.available) && (key !== 'contact' || appOnPhone));
  const currentSection = sections.some(({ key }) => key === section) ? section : 'subscription';

  const themeLabel = ui(THEMES.find(([value]) => value === themePreference)?.[1] || 'themeSystem');
  const indexValues = { subscription: isLoadingMe ? '' : subscriptionValue, appearance: themeLabel, privacy: '' };

  const content = {
    subscription: (
      <>
        <div className={`mt-glist${hasSubscription ? '' : ' mt-glist--icons'}`} aria-busy={isLoadingMe || undefined}>
          {!isLoadingMe && subscriptionRows}
        </div>
        {!isLoadingMe && subscriptionFoot && <p className="mt-glist__foot">{subscriptionFoot}</p>}
        {proExtras}
      </>
    ),
    appearance: (
      <>
        <div className="mt-glist">
          <div className="mt-looks" role="radiogroup" aria-labelledby="mt-acct-appearance" aria-describedby="mt-acct-theme-hint">
            {THEMES.map(([value, labelKey]) => (
              <label key={value} className="mt-look">
                <input
                  className="mt-look__input"
                  type="radio"
                  name="mt-acct-theme"
                  value={value}
                  checked={themePreference === value}
                  onChange={() => {
                    trackEvent(`account_theme_${value}`, `Account - Theme: ${value}`, 'account', true);
                    setThemePreference(value);
                  }}
                />
                <span className={`mt-look__preview mt-look__preview--${value}`} aria-hidden="true">
                  {value !== 'dark' && <MockScreen />}
                  {value !== 'light' && <MockScreen dark />}
                </span>
                <span className="mt-look__radio" aria-hidden="true">
                  <Check />
                </span>
                {ui(labelKey)}
              </label>
            ))}
          </div>
        </div>
        <p id="mt-acct-theme-hint" className="mt-glist__foot">
          {ui('themeHint')}
        </p>
      </>
    ),
    app: (
      <div className="mt-glist">
        {canOffer && (
          <button
            type="button"
            className="mt-glist__row mt-glist__btn"
            onClick={() => install('conta')}
            aria-labelledby="mt-acct-install-title"
            aria-describedby="mt-acct-install-hint"
          >
            <span className="mt-glist__k">
              <span id="mt-acct-install-title" className="mt-glist__title">
                {ui('install')}
              </span>
              <span id="mt-acct-install-hint" className="mt-glist__hint">
                {ui('installHint')}
              </span>
            </span>
            <ChevronRight className="mt-glist__end" aria-hidden="true" />
          </button>
        )}
        {/* Only in the installed app: push reaches nothing else */}
        {push.available && (
          <label className="mt-glist__row mt-glist__btn">
            <span className="mt-glist__k">
              <span className="mt-glist__title">{ui('pushTitle')}</span>
              <span id="mt-acct-push-hint" className="mt-glist__hint">
                {isPushBlocked ? ui('pushBlocked') : ui('pushHint')}
              </span>
            </span>
            <input
              className="mt-acct-switch"
              type="checkbox"
              role="switch"
              checked={push.subscribed}
              disabled={isPushBlocked || isPushSaving}
              aria-describedby="mt-acct-push-hint"
              onChange={(event) => togglePush(event.target.checked)}
            />
          </label>
        )}
      </div>
    ),
    privacy: (
      <>
        <div className="mt-glist mt-glist--icons">
          <label className="mt-glist__row mt-glist__btn">
            <span className="mt-glist__sq" aria-hidden="true">
              <BarChart3 />
            </span>
            <span className="mt-glist__k">{account('cookieAnalytics')}</span>
            <input
              className="mt-acct-switch"
              type="checkbox"
              role="switch"
              checked={cookiesEnabled}
              aria-describedby="mt-acct-cookies-hint"
              onChange={(event) => {
                const value = event.target.checked;
                setCookiesEnabled(value);
                if (value) {
                  grantConsent();
                  trackEvent('account_cookies_enabled', 'Account - Cookies Enabled', 'account', true);
                } else {
                  revokeConsent();
                }
              }}
            />
          </label>
          <Link className="mt-glist__row mt-glist__btn" to="/terms">
            <span className="mt-glist__sq" aria-hidden="true">
              <FileText />
            </span>
            <span className="mt-glist__k">{ui('terms')}</span>
            <ChevronRight className="mt-glist__end" aria-hidden="true" />
          </Link>
          <Link className="mt-glist__row mt-glist__btn" to="/privacy">
            <span className="mt-glist__sq" aria-hidden="true">
              <ShieldCheck />
            </span>
            <span className="mt-glist__k">{ui('privacyPolicy')}</span>
            <ChevronRight className="mt-glist__end" aria-hidden="true" />
          </Link>
        </div>
        <p id="mt-acct-cookies-hint" className="mt-glist__foot">
          {account('cookieAnalyticsDesc')}
        </p>
      </>
    ),
    contact: (
      <>
        <div className="mt-glist mt-glist--icons">
          <a className="mt-glist__row mt-glist__btn" href={`mailto:${t('companyMail')}`}>
            <span className="mt-glist__sq" aria-hidden="true">
              <Mail />
            </span>
            <span className="mt-glist__k">{t('companyMail')}</span>
            <ChevronRight className="mt-glist__end" aria-hidden="true" />
          </a>
          {i18n.language === 'pt' && (
            <a className="mt-glist__row mt-glist__btn" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
              <span className="mt-glist__sq" aria-hidden="true">
                <InstagramGlyph className="" />
              </span>
              <span className="mt-glist__k">{INSTAGRAM_HANDLE}</span>
              <ChevronRight className="mt-glist__end" aria-hidden="true" />
            </a>
          )}
        </div>
        <p className="mt-glist__foot">{t('components.Footer.company', { ...COMPANY_VARS, appName: t('appName') })}</p>
      </>
    ),
  };

  return (
    <div className="mt-root mt-root--app mt-acct-page">
      <h1 className="mt-acct__title">{ui('title')}</h1>

      <div className="mt-acct">
        {/* Desktop: a card holding who's signed in, the section index and sign-out. Phones: the identity card on
            top, sign-out at the bottom (the index hides; every section shows) */}
        <div className="mt-acct-index">
          <div className="mt-acct-me" aria-busy={isLoadingMe || undefined}>
            <span className="mt-acct-me__avatar" aria-hidden="true">
              {isLoadingMe ? '' : initial}
            </span>
            <span className="mt-acct-me__text">
              <span className="mt-acct-me__name">
                {me?.name}
                {isPro && <span className="mt-acct-me__pro">{ui('proTag')}</span>}
              </span>
              <span className="mt-acct-me__email">{me?.email}</span>
            </span>
          </div>

          <nav className="mt-acct-nav" aria-label={ui('sections')}>
            {sections.map(({ key, Icon }) => (
              <button
                key={key}
                type="button"
                className="mt-acct-nav__item"
                aria-current={currentSection === key || undefined}
                aria-controls={`mt-acct-${key}-section`}
                onClick={() => setSection(key)}
              >
                <span className="mt-glist__sq" aria-hidden="true">
                  <Icon />
                </span>
                {ui(key)}
                {indexValues[key] && <span className="mt-acct-nav__value">{indexValues[key]}</span>}
              </button>
            ))}
          </nav>

          <div className="mt-acct-out">
            <button type="button" className="mt-acct-out__btn" onClick={() => setSignOutOpen(true)}>
              <span className="mt-glist__sq" aria-hidden="true">
                <LogOut />
              </span>
              {ui('logout')}
            </button>
          </div>
        </div>

        <div className="mt-acct-pane">
          {sections.map(({ key }) => (
            <section
              key={key}
              id={`mt-acct-${key}-section`}
              className={`mt-acct-sec${currentSection === key ? ' is-current' : ''}`}
              aria-labelledby={`mt-acct-${key}`}
            >
              <h2 id={`mt-acct-${key}`} className="mt-acct-sec__title">
                {ui(key)}
              </h2>
              <p className="mt-acct-sec__lede">{ui(`${key}Lede`)}</p>
              {content[key]}
            </section>
          ))}
        </div>
      </div>

      <Modal
        isOpen={isSignOutOpen}
        onOpenChange={setSignOutOpen}
        placement="auto"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog mt-signout', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {(onClose) => (
            <div className="mt-dialog__body mt-signout__body">
              <span className="mt-signout__icon" aria-hidden="true">
                <LogOut />
              </span>
              <h2 className="mt-dialog__title">{ui('logoutTitle')}</h2>
              <p className="mt-dialog__text">{ui('logoutText')}</p>
              <div className="mt-signout__actions">
                <button type="button" className="mt-btn mt-btn--primary" onClick={logout}>
                  {ui('logout')}
                </button>
                <button type="button" className="mt-btn mt-btn--quiet" onClick={onClose}>
                  {ui('cancel')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
